import React, { useEffect, useState } from 'react';
import { db } from '../firebaseConfig'; 
import { doc, getDoc, setDoc } from "firebase/firestore"; 
import { Save, Tag, Zap, Megaphone, Settings, Percent, IndianRupee } from 'lucide-react'; 

const PricingController = () => {
    const [pricing, setPricing] = useState({
        offer_message: "Long-term growth ke liye best value chuniye aur paise bachaiye:",
        starter_q: 1499, starter_y: 4999,
        growth_q: 3999, growth_y: 12999,
        suite_q: 7499, suite_y: 24999,
        api_setup: 5000,
        discount_percentage: 20, 
        is_discount_active: false,
        // 🔥 NEW: Blast Credit Packs
        recharge_starter_price: 500, recharge_starter_credits: 500,
        recharge_value_price: 1000, recharge_value_credits: 1150,
        recharge_pro_price: 3000, recharge_pro_credits: 3600
    });
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        const fetchPricing = async () => {
            try {
                const docRef = doc(db, "system_config", "pricing");
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    setPricing(prev => ({ ...prev, ...docSnap.data() }));
                }
            } catch (error) {}
        };
        fetchPricing();
    }, []);

    const handleSave = async () => {
        setIsSaving(true);
        try {
            await setDoc(doc(db, "system_config", "pricing"), pricing, { merge: true });
            alert("✅ Dynamic Pricing, Discounts & Blast Packs Updated Successfully!");
        } catch (error) { alert("⚠️ Error saving prices."); }
        setIsSaving(false);
    };

    // 🔥 SMART CALCULATION: Quarterly price ka lagbhag 3.33x nikal kar 99 par end karna
    const calcYearly = (qtrPrice) => {
        if (!qtrPrice) return "";
        return Math.round((qtrPrice * 3.33) / 100) * 100 - 1;
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        let finalValue = value;
        
        if (type === 'checkbox') {
            finalValue = checked;
        } else if (name !== 'offer_message') {
            finalValue = Number(value);
        }
        
        setPricing(prev => {
            const newPricing = { ...prev, [name]: finalValue };

            // 🔥 AUTO-FILL YEARLY: Agar Quarterly type ho raha hai, toh Yearly khud update hoga
            if (name === 'starter_q') newPricing.starter_y = calcYearly(finalValue);
            if (name === 'growth_q') newPricing.growth_y = calcYearly(finalValue);
            if (name === 'suite_q') newPricing.suite_y = calcYearly(finalValue);

            return newPricing;
        });
    };

    // 🔥 LIVE DISCOUNT PREVIEW RENDERER
    const renderDiscountPreview = (price) => {
        if (!pricing.is_discount_active || !pricing.discount_percentage || !price) return null;
        
        const discountedPrice = Math.round(price - (price * (pricing.discount_percentage / 100)));
        return (
            <div className="mt-2 flex items-center justify-between bg-amber-50 border border-amber-200 px-2 py-1.5 rounded-lg animate-in fade-in slide-in-from-top-2">
                <span className="text-[9px] md:text-[10px] font-black text-amber-600 uppercase tracking-widest">🔥 Final</span>
                <span className="text-xs md:text-sm font-black text-amber-600">₹{discountedPrice}</span>
            </div>
        );
    };

    return (
        <div className="max-w-4xl mx-auto animate-in fade-in duration-500 space-y-4 md:space-y-6 pb-20">
            
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end mb-4 md:mb-6 border-b border-slate-200 pb-4 md:pb-6 gap-4">
                <div>
                    <h2 className="text-xl md:text-2xl font-black text-slate-900 tracking-tight uppercase flex items-center gap-2">
                        <Tag className="text-indigo-600 w-5 h-5 md:w-6 md:h-6"/> Pricing Engine
                    </h2>
                    <p className="text-xs md:text-sm font-medium text-slate-500 mt-1">Control bot pricing, discounts and live offers globally.</p>
                </div>
                <button 
                    onClick={handleSave} 
                    disabled={isSaving}
                    className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-700 text-white font-black px-6 md:px-8 py-2.5 md:py-3 rounded-xl shadow-md flex items-center justify-center gap-2 uppercase text-[9px] md:text-[10px] tracking-widest transition-all disabled:opacity-50"
                >
                    {isSaving ? "Deploying..." : <><Save size={14} className="md:w-4 md:h-4"/> Deploy Updates</>}
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                {/* LIVE PROMO MESSAGE */}
                <div className="bg-white p-4 md:p-6 rounded-2xl shadow-sm border border-slate-200 flex flex-col">
                    <h3 className="text-xs md:text-sm font-black text-slate-800 uppercase tracking-widest flex items-center gap-1.5 md:gap-2 mb-3 md:mb-4">
                        <Megaphone size={14} className="text-rose-500 md:w-4 md:h-4"/> Live Promotional Offer
                    </h3>
                    <label className="text-[9px] md:text-[10px] font-bold text-slate-400 uppercase tracking-widest block mb-1.5 md:mb-2">Bot Menu Header Text</label>
                    <textarea 
                        name="offer_message"
                        value={pricing.offer_message || ""} 
                        onChange={handleChange}
                        rows="2"
                        className="w-full flex-1 bg-rose-50 border border-rose-200 text-rose-900 px-3 py-2.5 md:px-4 md:py-3 rounded-lg md:rounded-xl text-xs md:text-sm font-bold focus:border-rose-500 outline-none resize-none"
                        placeholder="e.g. 🎉 DIWALI MEGA SALE - FLAT 50% OFF!"
                    ></textarea>
                </div>

                {/* DISCOUNT ENGINE CARD */}
                <div className="bg-amber-50 p-4 md:p-6 rounded-2xl shadow-sm border border-amber-200">
                    <h3 className="text-xs md:text-sm font-black text-amber-800 uppercase tracking-widest flex items-center gap-1.5 md:gap-2 mb-3 md:mb-4">
                        <Percent size={14} className="text-amber-500 md:w-4 md:h-4"/> Flash Sale / Discount
                    </h3>
                    <div className="flex items-center justify-between gap-4">
                        <div className="flex-1">
                            <label className="text-[9px] md:text-[10px] font-bold text-amber-600 uppercase tracking-widest block mb-1.5 md:mb-2">Discount (%)</label>
                            <input 
                                type="number" 
                                name="discount_percentage" 
                                value={pricing.discount_percentage || ''} 
                                onChange={handleChange} 
                                className="w-full bg-white border border-amber-200 px-3 py-2.5 md:px-4 md:py-3 rounded-lg md:rounded-xl text-xs md:text-sm font-bold text-amber-900 focus:border-amber-500 outline-none" 
                            />
                        </div>
                        <div className="flex flex-col items-center">
                            <label className="text-[9px] md:text-[10px] font-bold text-amber-600 uppercase tracking-widest block mb-1.5 md:mb-2">Sale Status</label>
                            <label className="relative inline-flex items-center cursor-pointer mt-1">
                                <input 
                                    type="checkbox" 
                                    name="is_discount_active" 
                                    checked={pricing.is_discount_active || false} 
                                    onChange={handleChange} 
                                    className="sr-only peer" 
                                />
                                <div className="w-11 h-6 bg-slate-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-500"></div>
                            </label>
                        </div>
                    </div>
                    <p className="text-[10px] md:text-xs text-amber-600/80 mt-2 md:mt-3 font-medium">Turn ON to automatically cut prices and show real-time sale values below.</p>
                </div>
            </div>

            {/* ========================================== */}
            {/* 🪙 NAYA: WHATSAPP BLAST CREDIT PACKS */}
            {/* ========================================== */}
            <div className="bg-slate-900 p-5 md:p-6 rounded-2xl shadow-sm border border-slate-800">
                <h3 className="text-xs md:text-sm font-black text-white uppercase tracking-widest flex items-center gap-1.5 md:gap-2 mb-4 md:mb-6 border-b border-slate-700 pb-3">
                    <Zap size={16} className="text-emerald-400"/> Blast Credit Packs
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Starter */}
                    <div className="bg-slate-800 p-4 rounded-xl border border-slate-700">
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3">Starter Pack</p>
                        <div className="space-y-3">
                            <div>
                                <label className="text-[9px] font-bold text-slate-500">Client Pays (₹)</label>
                                <div className="relative mt-1">
                                    <IndianRupee size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
                                    <input type="number" name="recharge_starter_price" value={pricing.recharge_starter_price || ''} onChange={handleChange} className="w-full pl-8 pr-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-xs font-bold text-white outline-none focus:border-indigo-500" />
                                </div>
                            </div>
                            <div>
                                <label className="text-[9px] font-bold text-emerald-500">Credits Given</label>
                                <input type="number" name="recharge_starter_credits" value={pricing.recharge_starter_credits || ''} onChange={handleChange} className="w-full px-3 py-2 bg-emerald-900/30 border border-emerald-500/30 rounded-lg text-xs font-bold text-emerald-400 outline-none focus:border-emerald-500 mt-1" />
                            </div>
                        </div>
                    </div>

                    {/* Value */}
                    <div className="bg-indigo-900/40 p-4 rounded-xl border border-indigo-500/50 relative">
                        <div className="absolute top-0 right-0 bg-indigo-500 text-white text-[8px] font-black px-2 py-0.5 rounded-bl-lg uppercase tracking-widest">Most Popular</div>
                        <p className="text-[10px] font-black text-indigo-300 uppercase tracking-widest mb-3 mt-1">Value Pack</p>
                        <div className="space-y-3">
                            <div>
                                <label className="text-[9px] font-bold text-indigo-400/70">Client Pays (₹)</label>
                                <div className="relative mt-1">
                                    <IndianRupee size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-indigo-400"/>
                                    <input type="number" name="recharge_value_price" value={pricing.recharge_value_price || ''} onChange={handleChange} className="w-full pl-8 pr-3 py-2 bg-indigo-950 border border-indigo-500/50 rounded-lg text-xs font-bold text-white outline-none focus:border-indigo-400" />
                                </div>
                            </div>
                            <div>
                                <label className="text-[9px] font-bold text-emerald-400">Credits Given (Bonus)</label>
                                <input type="number" name="recharge_value_credits" value={pricing.recharge_value_credits || ''} onChange={handleChange} className="w-full px-3 py-2 bg-emerald-900/50 border border-emerald-500/50 rounded-lg text-xs font-bold text-emerald-400 outline-none focus:border-emerald-400 mt-1" />
                            </div>
                        </div>
                    </div>

                    {/* Pro */}
                    <div className="bg-slate-800 p-4 rounded-xl border border-slate-700">
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3">Pro Pack</p>
                        <div className="space-y-3">
                            <div>
                                <label className="text-[9px] font-bold text-slate-500">Client Pays (₹)</label>
                                <div className="relative mt-1">
                                    <IndianRupee size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
                                    <input type="number" name="recharge_pro_price" value={pricing.recharge_pro_price || ''} onChange={handleChange} className="w-full pl-8 pr-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-xs font-bold text-white outline-none focus:border-indigo-500" />
                                </div>
                            </div>
                            <div>
                                <label className="text-[9px] font-bold text-emerald-500">Credits Given (Mega Bonus)</label>
                                <input type="number" name="recharge_pro_credits" value={pricing.recharge_pro_credits || ''} onChange={handleChange} className="w-full px-3 py-2 bg-emerald-900/30 border border-emerald-500/30 rounded-lg text-xs font-bold text-emerald-400 outline-none focus:border-emerald-500 mt-1" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* ========================================== */}
            {/* 💎 SAAS SUBSCRIPTION PLANS */}
            {/* ========================================== */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
                {/* STARTER */}
                <div className="bg-white p-4 md:p-6 rounded-2xl shadow-sm border border-slate-200 space-y-3 md:space-y-4 flex flex-col">
                    <h3 className="text-[10px] md:text-xs font-black text-slate-800 uppercase tracking-widest border-b border-slate-100 pb-2 md:pb-3">🥉 Starter</h3>
                    <div className="flex flex-row md:flex-col gap-3 md:gap-4 flex-1">
                        <div className="flex-1">
                            <label className="text-[9px] md:text-[10px] font-bold text-slate-400 uppercase tracking-widest block mb-1">Qtrly (₹)</label>
                            <input type="number" name="starter_q" value={pricing.starter_q || ''} onChange={handleChange} className="w-full bg-slate-50 border border-slate-200 px-3 md:px-4 py-2 md:py-3 rounded-lg md:rounded-xl text-xs md:text-sm font-bold text-slate-700" />
                            {renderDiscountPreview(pricing.starter_q)}
                        </div>
                        <div className="flex-1">
                            <label className="text-[9px] md:text-[10px] font-bold text-slate-400 uppercase tracking-widest block mb-1">Yearly (₹)</label>
                            <input type="number" name="starter_y" value={pricing.starter_y || ''} onChange={handleChange} className="w-full bg-slate-50 border border-slate-200 px-3 md:px-4 py-2 md:py-3 rounded-lg md:rounded-xl text-xs md:text-sm font-bold text-slate-700" />
                            {renderDiscountPreview(pricing.starter_y)}
                        </div>
                    </div>
                </div>

                {/* GROWTH */}
                <div className="bg-indigo-50 p-4 md:p-6 rounded-2xl shadow-sm border border-indigo-100 space-y-3 md:space-y-4 relative overflow-hidden flex flex-col">
                    <div className="absolute top-0 right-0 bg-indigo-500 text-white text-[7px] md:text-[8px] font-black px-2 md:px-3 py-0.5 md:py-1 rounded-bl-lg uppercase tracking-widest">Best Seller</div>
                    <h3 className="text-[10px] md:text-xs font-black text-indigo-900 uppercase tracking-widest border-b border-indigo-200 pb-2 md:pb-3 mt-2 md:mt-0">🥈 Growth</h3>
                    <div className="flex flex-row md:flex-col gap-3 md:gap-4 flex-1">
                        <div className="flex-1">
                            <label className="text-[9px] md:text-[10px] font-bold text-indigo-400 uppercase tracking-widest block mb-1">Qtrly (₹)</label>
                            <input type="number" name="growth_q" value={pricing.growth_q || ''} onChange={handleChange} className="w-full bg-white border border-indigo-200 px-3 md:px-4 py-2 md:py-3 rounded-lg md:rounded-xl text-xs md:text-sm font-black text-indigo-700" />
                            {renderDiscountPreview(pricing.growth_q)}
                        </div>
                        <div className="flex-1">
                            <label className="text-[9px] md:text-[10px] font-bold text-indigo-400 uppercase tracking-widest block mb-1">Yearly (₹)</label>
                            <input type="number" name="growth_y" value={pricing.growth_y || ''} onChange={handleChange} className="w-full bg-white border border-indigo-200 px-3 md:px-4 py-2 md:py-3 rounded-lg md:rounded-xl text-xs md:text-sm font-black text-indigo-700" />
                            {renderDiscountPreview(pricing.growth_y)}
                        </div>
                    </div>
                </div>

                {/* SUITE */}
                <div className="bg-slate-900 p-4 md:p-6 rounded-2xl shadow-xl border border-slate-700 space-y-3 md:space-y-4 flex flex-col">
                    <h3 className="text-[10px] md:text-xs font-black text-slate-100 uppercase tracking-widest border-b border-slate-700 pb-2 md:pb-3">🥇 Full Suite</h3>
                    <div className="flex flex-row md:flex-col gap-3 md:gap-4 flex-1">
                        <div className="flex-1">
                            <label className="text-[9px] md:text-[10px] font-bold text-slate-400 uppercase tracking-widest block mb-1">Qtrly (₹)</label>
                            <input type="number" name="suite_q" value={pricing.suite_q || ''} onChange={handleChange} className="w-full bg-slate-800 border border-slate-600 px-3 md:px-4 py-2 md:py-3 rounded-lg md:rounded-xl text-xs md:text-sm font-bold text-white" />
                            {renderDiscountPreview(pricing.suite_q)}
                        </div>
                        <div className="flex-1">
                            <label className="text-[9px] md:text-[10px] font-bold text-slate-400 uppercase tracking-widest block mb-1">Yearly (₹)</label>
                            <input type="number" name="suite_y" value={pricing.suite_y || ''} onChange={handleChange} className="w-full bg-slate-800 border border-slate-600 px-3 md:px-4 py-2 md:py-3 rounded-lg md:rounded-xl text-xs md:text-sm font-bold text-white" />
                            {renderDiscountPreview(pricing.suite_y)}
                        </div>
                    </div>
                </div>
            </div>

           {/* API SETUP ADD-ON SECTION (Aapka purana code) */}
            <div className="bg-white p-4 md:p-6 rounded-2xl shadow-sm border border-slate-200 w-full md:w-1/2">
                <h3 className="text-[10px] md:text-sm font-black text-slate-800 uppercase tracking-widest flex items-center gap-1.5 md:gap-2 mb-3 md:mb-4">
                    <Settings size={14} className="text-slate-500 md:w-4 md:h-4"/> Meta API Setup Add-on
                </h3>
                <div>
                    <label className="text-[9px] md:text-[10px] font-bold text-slate-400 uppercase tracking-widest block mb-1">One-Time Setup Fee (₹)</label>
                    <div className="relative mt-1">
                        <IndianRupee size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
                        <input type="number" name="api_setup" value={pricing.api_setup || ''} onChange={handleChange} className="w-full pl-8 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm font-bold outline-none focus:border-indigo-500" />
                    </div>
                    <p className="text-[8px] md:text-[10px] text-slate-400 mt-1.5 md:mt-2 font-medium">Billed when users without an API Token try to launch a Blast.</p>
                </div>
            </div>
            
            {/* 🔥 NAYA: BOTTOM SAVE BUTTON 🔥 */}
            <div className="pt-8 mt-8 border-t border-slate-200 flex flex-col items-center justify-center">
                <button 
                    onClick={handleSave} 
                    disabled={isSaving}
                    className="w-full md:w-auto bg-slate-900 hover:bg-indigo-600 text-white font-black px-12 py-4 rounded-2xl shadow-xl flex items-center justify-center gap-3 uppercase text-xs tracking-widest transition-all disabled:opacity-50"
                >
                    {isSaving ? "Deploying Updates..." : <><Save size={18} /> Save & Deploy All Changes</>}
                </button>
                <p className="text-[10px] text-slate-400 font-bold mt-3 uppercase tracking-widest">Changes will reflect instantly on all client dashboards</p>
            </div>

        </div>
    );
};

export default PricingController;
            
